import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import relativeTime from "dayjs/plugin/relativeTime";
import { SUPPORTED_LANGUAGES } from "@fysaro/shared";

import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/it";
import "dayjs/locale/fr";

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(relativeTime);

export const DAYJS_LOCALES: Record<string, string> = {};

Object.keys(SUPPORTED_LANGUAGES).forEach((lang) => {
  const short = lang.split("-")[0];
  DAYJS_LOCALES[lang] = short; // Guarda "en-GB": "en"
  DAYJS_LOCALES[short] = short; // Guarda "en": "en" ðŸ‘ˆ Â¡Esto salva el dí­a!
});

/**
 * 🗺️ Extrae de forma ultra-segura el locale raí­z de Day.js
 * Soporta "en-GB", "en_US", "EN", "en" -> siempre devolverá "en"
 */
const getSafeLocale = (lang?: string): string => {
  if (!lang) return "es";
  
  // 1. Limpieza inicial a minúsculas y normalización de guiones
  const normalized = lang.toLowerCase().replace("_", "-").trim();
  
  // 2. Intento de match directo (Ej: "en-gb")
  if (DAYJS_LOCALES[normalized]) return DAYJS_LOCALES[normalized];
  
  // 3. Extracción de la raí­z de dos letras (Ej: "en-gb" -> "en")
  const shortRoot = normalized.split("-")[0];
  if (DAYJS_LOCALES[shortRoot]) return DAYJS_LOCALES[shortRoot];
  
  // Fallback absoluto por defecto
  return "es";
};

/**
 * 📅 1. Formato Largo Completo (Ej: "miércoles, 10 de junio de 2026" / "Wednesday, June 10, 2026")
 * Reemplaza los parches manuales de `BookingSummary.tsx`
 */
export function formatLongDate(isoOrDate: string | Date, lang: string = "es"): string {
  const locale = getSafeLocale(lang);
  // Usamos "dddd, LL" para que meta automáticamente el dí­a de la semana y la fecha estructurada nativa del idioma
  // Booking flow dates are calendar values (YYYY-MM-DD), not instants in a
  // timezone. Parse them as UTC directly so Europe/Madrid never rolls them
  // back to the previous day before formatting.
  return dayjs.utc(isoOrDate).locale(locale).format("dddd, D MMMM YYYY");
}

/**
 * 📅 2. Formato Estándar Profesional (Ej: "10 Junio, 2026" / "10 June, 2026")
 * Reemplaza el parche de `ProfessionalDetailsModal.tsx`
 */
export function formatProfessionalDate(isoOrDate: string | Date, lang: string = "es"): string {
  const locale = getSafeLocale(lang);
  return dayjs(isoOrDate).utc().locale(locale).format("DD MMMM, YYYY");
}

/**
 * 📊 3. Formato Corto para Gráficos/Cards Públicas (Ej: "25 dic" / "25 Dec")
 * Reemplaza el parche peligroso de `PublicClinicOverview.tsx`
 */
export function formatShortDayMonth(dateStr: string, lang: string = "es"): string {
  const locale = getSafeLocale(lang);
  let instance = dayjs(dateStr);

  // Replicamos la lógica inteligente por si viene solo "12-25" (MM-DD)
  if (dateStr.includes("-") && dateStr.length === 5) {
    const currentYear = new Date().getFullYear();
    instance = dayjs(`${currentYear}-${dateStr}`);
  }

  if (!instance.isValid()) return dateStr;
  return instance.utc().locale(locale).format("D MMM");
}

/**
 * 📅 4. Formateador de Mes en curso para Calendarios (Ej: "2026-06")
 * Reemplaza el estado inicial de `BookingDatePage.tsx`
 */
export function getCurrentMonthStr(): string {
  return dayjs().format("YYYY-MM");
}

/**
 * â° 5. Devuelve el rango de horas formateado limpiamente (Ej: "14:00 - 15:00")
 * * Mantiene el parámetro 'lang' para flujos así­ncronos como el enví­o de invitaciones/correos.
 */
export function formatBookingTimeRange(
  startIsoOrDate: string | Date,
  endIsoOrDate: string | Date,
  lang: string = "es",
): string {
  const locale = getSafeLocale(lang);
  // Aplicamos el locale correspondiente a cada instancia de Day.js
  const start = dayjs(startIsoOrDate).utc().locale(locale).format("HH:mm");
  const end = dayjs(endIsoOrDate).utc().locale(locale).format("HH:mm");
  
  return `${start} - ${end}`;
}

/**
 * 📅 Devuelve la fecha formateada en formato largo (Ej: "10 de junio de 2026")
 */
export function formatBookingDate(isoOrDate: string | Date, lang: string = "es", formatStr: string = "LL"): string {
  return dayjs(isoOrDate).utc().locale(getSafeLocale(lang)).format(formatStr);
}

/**
 * 📅 Devuelve la fecha en formato ISO corto (Ej: "2026-06-20").
 * Ideal para inicializar inputs de tipo date o valores de formularios.
 */
export function formatISODate(isoOrDate: string | Date): string {
  return dayjs(isoOrDate).utc().format("YYYY-MM-DD");
}

/**
 * â° Devuelve una hora formateada limpiamente (Ej: "14:30")
 * Soporta idioma por si en el futuro el formato cambia según la región (AM/PM).
 */
export function formatSingleTime(isoOrDate: string | Date, lang: string = "es"): string {
  const locale = getSafeLocale(lang);
  return dayjs(isoOrDate).utc().locale(locale).format("HH:mm");
}

/**
 * ðŸ·ï¸ Devuelve el mes abreviado en 3 letras y limpio de puntos de formato (Ej: "DIC", "JUN")
 */
export function formatShortMonthLabel(isoOrDate: string | Date, lang: string = "es"): string {
  return dayjs(isoOrDate).utc().locale(getSafeLocale(lang)).format("MMM").replace(".", "").toUpperCase();
}

/**
 * ðŸ”¢ Devuelve el número de dí­a del mes con dos dí­gitos (Ej: "05", "24")
 */
export function formatDayNumber(isoOrDate: string | Date): string {
  return dayjs(isoOrDate).utc().format("DD");
}

/**
 * â±ï¸ Devuelve el tiempo relativo internacionalizado (Ej: "en 3 dí­as" / "in 3 days")
 */
export function formatFromNow(isoOrDate: string | Date, lang: string = "es"): string {
  return dayjs(isoOrDate).utc().locale(getSafeLocale(lang)).fromNow();
}

/**
 * ðŸŽ¯ Evalúa si una fecha corresponde al dí­a de hoy
 */
export function checkIsToday(isoOrDate: string | Date): boolean {
  return dayjs(isoOrDate).utc().isToday();
}

/**
 * ðŸŽ¯ Evalúa si una fecha corresponde al dí­a de mañana
 */
export function checkIsTomorrow(isoOrDate: string | Date): boolean {
  return dayjs(isoOrDate).utc().isTomorrow();
}

export function setDayjsLocale(lang: string) {
  const locale = getSafeLocale(lang); // ðŸ‘ˆ Usamos el extractor agresivo
  dayjs.locale(locale);
}
